import React, { useState, useEffect } from 'react';
import { SEOHead, getFAQSchema } from '../components/common/SEOHead';
import { Breadcrumbs } from '../components/common/Breadcrumbs';
import { getFaqs } from '../lib/api';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { LeadFormDialog } from '../components/common/LeadFormDialog';

export default function FAQPage() {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showQuoteForm, setShowQuoteForm] = useState(false);

  useEffect(() => {
    getFaqs()
      .then(res => setFaqs(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const schemaData = faqs.length > 0 ? getFAQSchema(faqs) : null;

  return (
    <>
      <SEOHead
        title="FAQ - Frequently Asked Questions"
        description="Find answers to common questions about wholesale fragrance purchasing, shipping, MOQ, authenticity, and partnership with Passion of Aroma."
        canonical="https://passionofaroma.com/faq"
        schemaData={schemaData}
      />

      {/* Hero */}
      <section className="pt-32 pb-16 bg-[#0B1F3B]" data-testid="faq-hero">
        <div className="container-custom">
          <Breadcrumbs items={[{ label: 'FAQ' }]} />
          <div className="mt-6 max-w-2xl">
            <span className="label-text mb-4 block">Support</span>
            <h1 className="heading-xl text-white mb-4">Frequently Asked Questions</h1>
            <p className="text-gray-300 text-lg">
              Find answers to common questions about our products, ordering process, 
              and partnership opportunities.
            </p>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-[#F7F6F3]" data-testid="faq-content">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            {loading ? (
              <div className="space-y-4">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="skeleton h-16" />
                ))}
              </div>
            ) : faqs.length > 0 ? (
              <Accordion type="single" collapsible className="space-y-4">
                {faqs.map((faq, index) => (
                  <AccordionItem
                    key={faq.id}
                    value={`item-${index}`}
                    className="bg-white border border-gray-100 px-6"
                    data-testid={`faq-item-${index}`}
                  >
                    <AccordionTrigger className="text-left py-5 hover:no-underline">
                      <span className="heading-sm text-[#0B1F3B]">{faq.question}</span>
                    </AccordionTrigger>
                    <AccordionContent className="text-gray-600 pb-5">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            ) : (
              <div className="text-center py-12 bg-white">
                <p className="text-gray-500">No FAQs available yet.</p>
              </div>
            )}
          </div>

          {/* CTA */}
          <div className="max-w-3xl mx-auto mt-16 text-center bg-white p-12">
            <h2 className="heading-md mb-4">Still Have Questions?</h2>
            <p className="text-gray-600 mb-8">
              Can't find what you're looking for? Our team is here to help.
            </p>
            <button
              onClick={() => setShowQuoteForm(true)}
              className="btn-primary"
              data-testid="faq-contact-btn"
            >
              Contact Us
            </button>
          </div>
        </div>
      </section>

      <LeadFormDialog
        isOpen={showQuoteForm}
        onClose={() => setShowQuoteForm(false)}
        sourcePageUrl="/faq"
        showBuyerType={false}
        title="Contact Us"
        subtitle="Ask us anything - we're happy to help"
      />
    </>
  );
}
